import { Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Reveal } from "@/components/Reveal";

const LAYER_KEYS = ["llm", "app", "algo", "edge"] as const;

const NOISE =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")";

type Variant = "full" | "compact";

export function PlatformArchitecture({
  className = "",
  variant = "full",
}: {
  className?: string;
  variant?: Variant;
}) {
  const { t } = useTranslation();
  const layers = LAYER_KEYS.map((key) => ({
    key,
    concept: t(`arch:concept.${key}`),
    name: t(`arch:layers.${key}`),
    desc: t(`arch:desc.${key}`),
    modules: t(`arch:${key}Modules`, { returnObjects: true }) as unknown as string[],
  }));
  const last = layers.length - 1;

  return (
    <div
      className={`relative overflow-hidden rounded-[1.75rem] border border-white/10 bg-[#070b10] text-white shadow-[0_40px_120px_-50px_rgba(1,120,254,0.55)] ${className}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_80%_at_12%_-10%,rgba(1,120,254,0.3),transparent_60%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(90%_70%_at_100%_115%,rgba(0,200,247,0.16),transparent_55%)]" />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.6) 1px,transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage: "radial-gradient(circle at 25% 15%, black, transparent 75%)",
          WebkitMaskImage: "radial-gradient(circle at 25% 15%, black, transparent 75%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.045] mix-blend-overlay"
        style={{ backgroundImage: NOISE }}
      />

      {variant === "compact" ? (
        <div className="relative px-5 py-6 sm:px-8 sm:py-9">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {layers.map((layer, i) => {
              const isCrown = i === 0;
              return (
                <Reveal key={layer.key} delay={i * 0.08}>
                  <div className="group h-full rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors duration-300 hover:border-brand/30">
                    <div className="flex items-center justify-between">
                      <span className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-white/40">
                        {layer.name}
                      </span>
                      <span aria-hidden className="font-display text-sm font-bold text-white/20">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3
                      className={`mt-3 font-display text-[1.7rem] font-bold leading-tight tracking-tight ${
                        isCrown ? "text-brand-gradient" : "text-white"
                      }`}
                    >
                      {layer.concept}
                    </h3>
                    <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
                      {layer.modules.map((m) => (
                        <span
                          key={m}
                          className="flex items-center gap-1.5 text-[0.78rem] text-white/55 transition-colors duration-300 group-hover:text-white/75"
                        >
                          <span
                            aria-hidden
                            className={`h-1 w-1 shrink-0 rounded-full ${
                              isCrown ? "bg-brand-cyan" : "bg-brand/70"
                            }`}
                          />
                          {m}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
          <div className="mt-6 border-t border-white/10 pt-6 text-center sm:text-right">
            <Link
              to="/products"
              className="group inline-flex items-center gap-1.5 text-sm font-semibold text-brand-cyan"
            >
              {t("arch:cta")}
              <ArrowForwardIcon
                style={{ fontSize: 18 }}
                className="transition-transform group-hover:translate-x-1"
              />
            </Link>
          </div>
        </div>
      ) : (
        <div className="relative px-6 py-10 sm:px-12 sm:py-16">
          <div className="relative">
            <motion.span
              aria-hidden
              initial={{ scaleY: 0, opacity: 0 }}
              whileInView={{ scaleY: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              className="absolute left-0 top-3 bottom-3 hidden w-px origin-bottom bg-gradient-to-b from-brand-cyan via-brand to-transparent sm:block"
            />

            <div className="flex flex-col">
              {layers.map((layer, i) => {
                const isCrown = i === 0;
                return (
                  <Reveal
                    key={layer.key}
                    delay={(last - i) * 0.12}
                    className={`group relative py-7 sm:py-10 sm:pl-12 ${
                      i === 0 ? "" : "border-t border-white/[0.07]"
                    }`}
                  >
                    <span
                      aria-hidden
                      className="pointer-events-none absolute right-1 top-1/2 hidden -translate-y-1/2 select-none font-display text-[6.5rem] font-bold leading-none text-white/[0.035] sm:block"
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>

                    <span
                      aria-hidden
                      className={`absolute left-0 top-1/2 hidden h-2 w-2 -translate-x-1/2 -translate-y-1/2 rounded-full sm:block ${
                        isCrown
                          ? "bg-brand-cyan shadow-[0_0_18px_5px_rgba(0,200,247,0.55)]"
                          : "bg-brand shadow-[0_0_12px_3px_rgba(1,120,254,0.5)]"
                      }`}
                    />

                    <div className="grid gap-x-12 gap-y-4 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1fr)] lg:items-center">
                      <div>
                        <span className="text-[0.7rem] font-semibold uppercase tracking-[0.32em] text-white/40">
                          {layer.name}
                        </span>
                        <h3
                          className={`mt-2.5 font-display font-bold leading-[0.92] tracking-tight ${
                            isCrown
                              ? "text-brand-gradient text-5xl sm:text-7xl"
                              : "text-white text-[2.75rem] sm:text-6xl"
                          }`}
                        >
                          {layer.concept}
                        </h3>
                      </div>
                      <div>
                        <p className="max-w-md text-sm leading-relaxed text-white/70 sm:text-base">
                          {layer.desc}
                        </p>
                        <div className="mt-4 flex flex-wrap items-center gap-x-2.5 gap-y-2 text-[0.78rem] text-white/45">
                          {layer.modules.map((m, j) => (
                            <Fragment key={m}>
                              {j > 0 && (
                                <span aria-hidden className="text-white/20">
                                  ·
                                </span>
                              )}
                              <span className="transition-colors duration-300 group-hover:text-white/80">
                                {m}
                              </span>
                            </Fragment>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
